import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import IdlePopup from "./IdlePopup";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  IDLE_COLUMNS,
  IDLE_CONFIG,
  IDLE_SEARCHABLE_FIELDS,
  IqamaExpiryLegend,
  formatDate
} from './IdleConstants';
import { isWithinMonths } from '../../../../../utils/dateFormat';

const Idle = ({
  globalData, // unused - can be removed
  setTrigger,
  trigger,
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  const [showPopup, setShowPopup] = useState(false);
  const [selectedId, setSelectedId] = useState([]);
  
  // Get token from localStorage (consider using context for better performance)
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Get data from SmartDownloadPagination via callback
  const [hasData, setHasData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [iqamaExpiringCount, setIqamaExpiringCount] = useState(0);

  const isIqamaExpiringSoon = (dateStr) => isWithinMonths(dateStr, 6);

  // Handle data load callback to track data availability
  const handleDataLoad = useCallback((data, paginationState) => {
    const dataExists = data && data.length > 0;
    setHasData(dataExists);
    setIsLoading(paginationState.isLoading);
    
    // Count iqama expiring within 6 months
    if (data && data.length > 0) {
      const count = data.filter((item) => isIqamaExpiringSoon(item.iqama_expiry_date_g)).length;
      setIqamaExpiringCount(count);
    } else {
      setIqamaExpiringCount(0);
    }
    
    // Clear selection if no data
    if (!dataExists) {
      setSelectedId([]);
    }
  }, []);

  // Optimized selection handlers
  const handleSelectionChange = useCallback((candidateId, isChecked) => {
    setSelectedId(isChecked ? [candidateId] : []);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedId([]);
  }, []);

  // Memoized custom cell renderer with stable reference
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    const serialNumber = (currentPage - 1) * pageSize + rowIndex + 1;

    switch (column.key) {
      case 'serial':
        return serialNumber;

      case 'select':
        return (
          <input
            type="checkbox"
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            onChange={(event) => handleSelectionChange(item.candidate_id, event.target.checked)}
            checked={selectedId.includes(item.candidate_id)}
          />
        );

      case 'visa_provider':
        return item.visa_provider || item.Overseas_visa_provider || '-----';

      case 'arrival_date':
      case 'mobilization_date':
      case 'demobilization_date':
        return formatDate(item[column.key]);

      case 'iqama_expiry_date_g':
        return formatDate(item[column.key]);

      case 'demobilization_comments':
        return (
          <div className="relative group">
            <button className="text-blue-600 hover:text-blue-800">
              View comments
            </button>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white shadow-xl rounded-lg ring-1 ring-slate-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 max-w-xs whitespace-normal">
              {item.demobilization_comments || "-----"}
            </div>
          </div>
        );

      case 'idle_aging':
        return item.idle_aging ? `${item.idle_aging} days` : 'N/A';

      case 'total_salary':
        return item.total_salary ? `${item.total_salary} SAR` : '-----';

      default:
        return item[column.key] || '-----';
    }
  }, [selectedId, handleSelectionChange]);

  // Memoized row styling with stable reference
  const getRowClassName = useCallback((item) => {
    if (isIqamaExpiringSoon(item.iqama_expiry_date_g)) return 'bg-red-50 border-l-4 border-red-500';
    return item.deadline ? 'bg-red-50 border-l-4 border-red-500' : '';
  }, []);

  // Optimized popup handlers
  const handlePopUpOpen = useCallback(() => {
    if (selectedId.length === 0) {
      dispatch(
        setAppError({
          msg: "At least one employee should be selected.",
        })
      );
      return;
    }
    setShowPopup(true);
  }, [selectedId.length, dispatch]);

  const handlePopUpClose = useCallback(() => {
    setShowPopup(false);
  }, []);

  // Stable additional actions with proper memoization - always show but disable when no data
  const additionalActions = useMemo(() => {
    const isDisabled = !hasData || isLoading || selectedId.length === 0;
    const actions = [];
    
    if (checkAccess("DEMOBILIZATION", "idle-edit")) {
      actions.push({
        label: "CHANGE STATUS",
        onClick: handlePopUpOpen,
        disabled: isDisabled,
        className: `px-4 rounded-md py-2 text-sm h-[36px] text-white ${
          isDisabled 
            ? 'cursor-not-allowed opacity-50' 
            : 'bg-red cursor-pointer hover:opacity-90'
        }`,
        style: isDisabled ? { backgroundColor: '#fca5a5' } : { backgroundColor: '#CF2623' }
      });
    }
    
    return actions;
  }, [hasData, isLoading, handlePopUpOpen, selectedId.length, dashboardData]);

  return (
    <div>
      <SmartDownloadPagination
        apiEndpoint={IDLE_CONFIG.API_ENDPOINT}
        downloadEndpoint={IDLE_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        columns={IDLE_COLUMNS}
        searchableFields={IDLE_SEARCHABLE_FIELDS}
        layoutType="table"
        enableHighlighting={true}
        renderCustomCell={renderCustomCell}
        getRowClassName={getRowClassName}
        additionalActions={additionalActions}
        legendComponent={<IqamaExpiryLegend count={iqamaExpiringCount} />}
        searchPlaceholder={IDLE_CONFIG.SEARCH_PLACEHOLDER}
        defaultPageSize={10}
        pageSizeOptions={[10, 50, 100, 500, 1000]}
        enableDownload={true}
        downloadFileName={IDLE_CONFIG.DOWNLOAD_FILENAME}
        noDataMessage={IDLE_CONFIG.NO_DATA_MESSAGE}
        onDataLoad={handleDataLoad}
        trigger={trigger}
      />

      {showPopup && (
        <IdlePopup
          data={selectedId}
          setShowPopup={handlePopUpClose}
          showPopup={showPopup}
          setTrigger={setTrigger}
          trigger={trigger}
          setSelectedId={clearSelection}
        />
      )}
    </div>
  );
};

Idle.displayName = 'Idle';

export default Idle;
